import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListFlowsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-52 Automations list.
 *
 * The automation builder and its run log were both reachable only by typing
 * an id. This is the list they were missing.
 *
 * Status is a column rather than a badge tucked into the name, because
 * "which of these is actually live" is the question somebody opens this
 * screen to answer.
 */

type Row = ListFlowsResult['flows'][number];

function AutomationsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'updated', direction: 'desc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListFlowsResult, ApiError>({
    queryKey: ['flows', 'automation'],
    queryFn: () => api.listFlows({ query: { palette: 'automation' } }),
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.flows ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'status'
        ? a.status.localeCompare(b.status) * direction
        : sort.field === 'updated'
          ? a.updatedAt.localeCompare(b.updatedAt) * direction
          : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'status',
        header: t('list.column.status'),
        sortField: 'status',
        essential: true,
        cell: (row) => row.status,
      },
      {
        key: 'updated',
        header: t('list.column.updated'),
        sortField: 'updated',
        hideOnCard: true,
        cell: (row) => <Num className="text-ink-meta">{formatRelativeDate(row.updatedAt, format)}</Num>,
      },
    ],
    [t, format],
  );

  return (
    <div className="flex w-full flex-col gap-2">
    {/* Fifty ready-made recipes, installed as drafts (F-094). */}
    <Link data-recipe-gallery to="/automations/templates" className="self-end text-dense text-bronze-text hover:text-ink">
      {t('recipes.browse')}
    </Link>
    <RecordList<Row>
      title={t('list.automations.title')}
      icon="Lightning"
      rowId={(row) => row.flowId}
      recordHref={(row) => `/automations/${row.flowId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/automations/new')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.automations.empty.title')}
      emptyBody={t('list.automations.empty.body')}
      emptyIllustration="Lightning"
    />
    </div>
  );
}

function AutomationsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Lightning"
      title={t('list.automations.empty.title')}
      body={t('list.automations.empty.body')}
      primary={{ label: t('action.new'), icon: 'Plus', href: '/automations/new' }}
    />
  );
}

function AutomationsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function AutomationsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Automations could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-52',
  route: '/automations',
  surface: 'page',
  title: 'Automations list',
  features: ['F-068'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'nav', item: 'automations' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: AutomationsListEmpty,
    loading: AutomationsListLoading,
    error: AutomationsListError,
  },
  component: AutomationsListScreen,
});

export default screen;
